import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@osd/ui/button';
import { EmptyState } from '@osd/ui/empty-state';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Table, type TableSortDirection } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import { api, ApiError } from '../lib/api';

export const Route = createFileRoute('/users')({
  component: UsersPage,
});

type SortKey = 'name' | 'email';

interface SortState {
  readonly key: SortKey;
  readonly direction: TableSortDirection;
}

export function UsersPage() {
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list(),
  });

  const [sort, setSort] = useState<SortState>({ key: 'name', direction: 'ascending' });

  const sortedUsers = useMemo(() => {
    if (!users.data) return [];
    const rows = [...users.data.users];
    const dir = sort.direction === 'ascending' ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.key === 'email') return a.email.localeCompare(b.email) * dir;
      return a.name.localeCompare(b.name) * dir;
    });
    return rows;
  }, [users.data, sort]);

  if (users.isLoading) return <Text muted>Loading users…</Text>;
  if (users.isError) {
    const e = users.error;
    const msg = e instanceof ApiError ? `API ${e.status}` : (e as Error).message;
    return <Text muted>Failed to load users: {msg}</Text>;
  }
  if (!users.data) return <Text>No data.</Text>;

  function directionFor(key: SortKey): TableSortDirection {
    return sort.key === key ? sort.direction : 'none';
  }

  function toggleSort(key: SortKey): void {
    setSort((current) => {
      if (current.key !== key) return { key, direction: 'ascending' };
      const next: TableSortDirection =
        current.direction === 'ascending' ? 'descending' : 'ascending';
      return { key, direction: next };
    });
  }

  return (
    <Stack gap={5}>
      <header>
        <Heading level={1}>Users</Heading>
        <Text muted>{users.data.count} total</Text>
      </header>

      {users.data.users.length === 0 ? (
        <EmptyState
          title="No users yet."
          description="Workspace is empty. Once invitations are accepted, members will appear here."
        />
      ) : (
        <Table columns="1fr 2fr 120px" caption="Workspace members">
          <Table.Header>
            <Table.Row>
              <Table.SortableHead
                direction={directionFor('name')}
                onSort={() => toggleSort('name')}
              >
                Name
              </Table.SortableHead>
              <Table.SortableHead
                direction={directionFor('email')}
                onSort={() => toggleSort('email')}
              >
                Email
              </Table.SortableHead>
              <Table.Head>Actions</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedUsers.map((u) => (
              <UserRow key={u.id} user={u} />
            ))}
          </Table.Body>
        </Table>
      )}
    </Stack>
  );
}

/**
 * Single user row — extracted so the Delete mutation + confirmation
 * state stay out of the list-render path. `useMutation` invalidates
 * the ['users'] query on success, which refetches the list and
 * removes the deleted row automatically.
 */
function UserRow({
  user,
}: {
  readonly user: { readonly id: string; readonly name: string; readonly email: string };
}): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const deleteUser = useMutation({
    mutationFn: (id: string) =>
      fetch(`/api/users/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'include',
      }).then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      setConfirming(false);
    },
  });

  return (
    <Table.Row>
      <Table.Cell data-label="Name">
        <strong>{user.name}</strong>
      </Table.Cell>
      <Table.Cell data-label="Email">{user.email}</Table.Cell>
      <Table.Cell data-label="Actions">
        {confirming ? (
          <Stack direction="row" gap={2} align="center">
            <Text muted>Delete?</Text>
            <Button
              variant="danger"
              onClick={() => deleteUser.mutate(user.id)}
              disabled={deleteUser.isPending}
            >
              {deleteUser.isPending ? 'Deleting…' : 'Confirm'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={deleteUser.isPending}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Delete
          </Button>
        )}
        {deleteUser.isError ? (
          <Text muted>Failed: {(deleteUser.error as Error).message}</Text>
        ) : null}
      </Table.Cell>
    </Table.Row>
  );
}
